import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AtSign, KeyRound, Mail, Send } from 'lucide-react';
import { Botao, Selo } from './base';
import { Estado } from './estados';
import { Entrada, Rotulo } from './formulario';
import { Confirmar } from './sobreposicao';
import { useToast } from './toast';
import { AcessoCriadoModal, type DadosAcessoCriado } from './acesso-criado';
import { get, mensagemDeErro, post, traduzirErro } from '../nucleo/api';
import { data, email as formatarEmail, nomeDeUsuario } from '../nucleo/formato';

/** Val7 (29/10, ficha `FUN_formas-de-acesso`): o que `GET /auth/formas-acesso/:usuario_id` devolve. */
export interface DadosFormasAcesso {
  usuario_id: string;
  nome: string;
  /** null na conta sem e-mail. */
  email: string | null;
  usuario: string;
  /** Efetivos: forma ativa (e, no e-mail, conta com e-mail). */
  login_email: boolean;
  login_usuario: boolean;
  sem_email: boolean;
  membro_ativo: boolean;
  /** Quando o membro está inativo (desligado ou desativado). */
  desligado_em: string | null;
  /** A trava da conta global (`guardaGestaoConta`) deixa quem vê mexer. */
  gerivel: boolean;
  recusa: { code: string; message: string } | null;
}

/** Os ícones das formas ATIVAS (lista de Membros e de Funcionários), cada um com o nome lido pelo leitor de tela. */
export function IconesFormas({ email, usuario }: { email: boolean; usuario: boolean }) {
  const { t } = useTranslation();
  if (!email && !usuario) return <span className="text-xs text-tinta-3">{t('formasAcesso.icones.nenhuma')}</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-tinta-2" data-formas-icones="">
      {email && (
        <span role="img" data-forma="email" aria-label={t('formasAcesso.icones.email')} title={t('formasAcesso.icones.email')}>
          <Mail className="size-4" aria-hidden />
        </span>
      )}
      {usuario && (
        <span role="img" data-forma="usuario" aria-label={t('formasAcesso.icones.usuario')} title={t('formasAcesso.icones.usuario')}>
          <AtSign className="size-4" aria-hidden />
        </span>
      )}
    </span>
  );
}

type Edicao = 'email' | 'usuario' | null;

/**
 * Val7 (29/10): o quadro "Formas de acesso", o mesmo no detalhe do membro (Admin > Membros) e na ficha do funcionário. Duas
 * formas (E-mail de login e Nome de usuário: Trocar, Desativar, Acrescentar) e a Senha (link ao e-mail para a pessoa criar a
 * própria; sem e-mail, senha provisória mostrada uma vez). Sempre ao menos uma forma ativa: o Desativar da última fica travado
 * com a explicação. A trava da conta global (o servidor manda `gerivel` e a recusa) e o membro desligado travam tudo.
 * `desligamento`: a data do desligamento da ficha (o aviso diz que veio do desligamento do funcionário).
 */
/** `semSenha` (val4, conta de demonstração sem login próprio): a linha da senha não aparece. */
export function FormasAcesso({ usuarioId, desligamento, semSenha }: { usuarioId: string; desligamento?: string | null; semSenha?: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const chave = ['/auth/formas-acesso', usuarioId];
  const q = useQuery({ queryKey: chave, queryFn: () => get<DadosFormasAcesso>(`/auth/formas-acesso/${usuarioId}`) });
  const [edicao, setEdicao] = useState<Edicao>(null);
  const [valor, setValor] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState(false);
  const [senhaGerada, setSenhaGerada] = useState<DadosAcessoCriado | null>(null);
  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: chave });
    await qc.invalidateQueries({ queryKey: ['/admin/membros'] });
    await qc.invalidateQueries({ queryKey: ['/pessoas/funcionarios'] });
    await qc.invalidateQueries({ queryKey: ['pessoas'] });
  };
  const falha = (e: unknown) => {
    setConfirmarSenha(false);
    toast.erro(mensagemDeErro(e));
  };
  const fechar = () => {
    setEdicao(null);
    setValor('');
  };
  const alterar = useMutation({
    mutationFn: (corpo: { email?: boolean; usuario?: boolean; nome_usuario?: string }) => post<DadosFormasAcesso>('/auth/formas-acesso', { usuario_id: usuarioId, ...corpo }),
    onSuccess: async () => {
      fechar();
      toast.sucesso(t('formasAcesso.salvo'));
      await recarregar();
    },
    onError: falha,
  });
  const trocarEmail = useMutation({
    mutationFn: (novo: string) => post('/auth/email/admin', { usuario_id: usuarioId, email: novo }),
    onSuccess: async () => {
      fechar();
      toast.sucesso(t('formasAcesso.emailEnviado'));
      await recarregar();
    },
    onError: falha,
  });
  const enviarLink = useMutation({
    mutationFn: () => post('/auth/senha-link', { usuario_id: usuarioId }),
    onSuccess: () => toast.sucesso(t('formasAcesso.linkEnviado')),
    onError: falha,
  });
  const gerarSenha = useMutation({
    mutationFn: () => post<DadosAcessoCriado>('/auth/senha-provisoria', { usuario_id: usuarioId }),
    onSuccess: (r) => {
      setConfirmarSenha(false);
      setSenhaGerada(r);
    },
    onError: falha,
  });

  return (
    <section data-formas-acesso="" className="flex min-w-0 flex-col gap-3">
      <div>
        <h3 className="kicker mb-1">{t('formasAcesso.titulo')}</h3>
        <p className="text-xs text-tinta-3">{t('formasAcesso.texto')}</p>
      </div>
      <Estado consulta={q}>
        {(d) => {
          const travado = !d.gerivel || !d.membro_ativo;
          const ativas = Number(d.login_email) + Number(d.login_usuario);
          const ultima = ativas <= 1;
          const ocupado = alterar.isPending || trocarEmail.isPending;
          const abrir = (qual: Exclude<Edicao, null>, inicial = '') => {
            setEdicao(qual);
            setValor(inicial);
          };
          const desativar = (forma: 'email' | 'usuario', ativa: boolean) =>
            ativa ? (
              <Botao tamanho="sm" variante="perigo" disabled={travado || ultima || ocupado} title={ultima ? t('formasAcesso.ultimaForma') : undefined} data-desativar={forma} onClick={() => alterar.mutate({ [forma]: false })}>
                {t('formasAcesso.desativar')}
              </Botao>
            ) : null;
          return (
            <>
              {!d.membro_ativo && (
                <p data-formas-desligado="" className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">
                  {t(desligamento ? 'formasAcesso.desligadoFicha' : 'formasAcesso.desligado', { data: data(desligamento ?? d.desligado_em) })}
                </p>
              )}
              {d.membro_ativo && !d.gerivel && d.recusa && (
                <p data-formas-recusa="" role="note" className="rounded-lg border border-laranja px-3 py-2 text-sm text-laranja">
                  {traduzirErro(d.recusa.code, d.recusa.message)}
                </p>
              )}
              <ul className="flex flex-col divide-y divide-borda rounded-xl border border-borda">
                <Linha
                  forma="email"
                  icone={<Mail className="size-4" aria-hidden />}
                  rotulo={t('formasAcesso.email')}
                  valor={d.sem_email || !d.email ? t('formasAcesso.semEmail') : formatarEmail(d.email)}
                  ativa={d.login_email}
                  acoes={
                    <>
                      {!d.sem_email && (
                        <Botao tamanho="sm" disabled={travado || ocupado} data-trocar="email" onClick={() => abrir('email')}>
                          {t('formasAcesso.trocar')}
                        </Botao>
                      )}
                      {desativar('email', d.login_email)}
                      {!d.login_email && (
                        <Botao tamanho="sm" disabled={travado || ocupado} data-acrescentar="email" onClick={() => (d.sem_email ? abrir('email') : alterar.mutate({ email: true }))}>
                          {t('formasAcesso.acrescentar')}
                        </Botao>
                      )}
                    </>
                  }
                  ajuda={d.login_email && !travado && !ultima ? t('formasAcesso.desativarEmailAjuda') : undefined}
                />
                <Linha
                  forma="usuario"
                  icone={<AtSign className="size-4" aria-hidden />}
                  rotulo={t('formasAcesso.usuario')}
                  valor={nomeDeUsuario(d.usuario)}
                  ativa={d.login_usuario}
                  acoes={
                    <>
                      {d.login_usuario && (
                        <Botao tamanho="sm" disabled={travado || ocupado} data-trocar="usuario" onClick={() => abrir('usuario', d.usuario)}>
                          {t('formasAcesso.trocar')}
                        </Botao>
                      )}
                      {desativar('usuario', d.login_usuario)}
                      {!d.login_usuario && (
                        <Botao tamanho="sm" disabled={travado || ocupado} data-acrescentar="usuario" onClick={() => abrir('usuario', d.usuario)}>
                          {t('formasAcesso.acrescentar')}
                        </Botao>
                      )}
                    </>
                  }
                />
                {!semSenha && (
                  <Linha
                    forma="senha"
                    icone={<KeyRound className="size-4" aria-hidden />}
                    rotulo={t('formasAcesso.senha')}
                    ajuda={d.sem_email ? t('formasAcesso.gerarSenhaTexto') : t('formasAcesso.linkTexto')}
                    acoes={
                      d.sem_email ? (
                        <Botao tamanho="sm" disabled={travado} data-senha="provisoria" onClick={() => setConfirmarSenha(true)}>
                          {t('formasAcesso.gerarSenha')}
                        </Botao>
                      ) : (
                        <Botao tamanho="sm" disabled={travado} carregando={enviarLink.isPending} icone={<Send className="size-4" />} data-senha="link" onClick={() => enviarLink.mutate()}>
                          {t('formasAcesso.enviarLink')}
                        </Botao>
                      )
                    }
                  />
                )}
              </ul>
              {ultima && !travado && (
                <p data-ultima-forma="" className="text-xs text-tinta-3">
                  {t('formasAcesso.ultimaForma')}
                </p>
              )}
              {edicao && !travado && (
                <form
                  data-formas-edicao={edicao}
                  className="flex flex-col gap-2 sm:flex-row sm:items-end"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const v = valor.trim();
                    if (!v) return;
                    if (edicao === 'email') trocarEmail.mutate(v);
                    else alterar.mutate({ usuario: true, ...(v !== d.usuario ? { nome_usuario: v } : {}) });
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <Rotulo
                      id={`formas-${edicao}-${usuarioId}`}
                      rotulo={edicao === 'email' ? t('formasAcesso.emailNovo') : t('formasAcesso.usuarioNovo')}
                      ajuda={edicao === 'email' ? t('formasAcesso.emailAjuda') : t('formasAcesso.usuarioAjuda')}
                    >
                      <Entrada
                        id={`formas-${edicao}-${usuarioId}`}
                        type={edicao === 'email' ? 'email' : 'text'}
                        autoComplete="off"
                        value={valor}
                        onChange={(e) => setValor(e.target.value)}
                      />
                    </Rotulo>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Botao type="button" variante="fantasma" onClick={fechar}>
                      {t('formasAcesso.cancelar')}
                    </Botao>
                    <Botao type="submit" variante="primario" carregando={ocupado} disabled={!valor.trim() || (edicao === 'email' && !valor.includes('@'))}>
                      {t('formasAcesso.salvar')}
                    </Botao>
                  </div>
                </form>
              )}
            </>
          );
        }}
      </Estado>
      <Confirmar
        aberto={confirmarSenha}
        aoFechar={() => setConfirmarSenha(false)}
        aoConfirmar={() => gerarSenha.mutate()}
        carregando={gerarSenha.isPending}
        perigoso
        titulo={t('formasAcesso.gerarSenha')}
        texto={t('formasAcesso.gerarSenhaConfirmar')}
      />
      <AcessoCriadoModal dados={senhaGerada} aoFechar={() => setSenhaGerada(null)} />
    </section>
  );
}

function Linha({ forma, icone, rotulo, valor, ativa, acoes, ajuda }: { forma: string; icone: ReactNode; rotulo: string; valor?: string; ativa?: boolean; acoes: ReactNode; ajuda?: string }) {
  const { t } = useTranslation();
  return (
    <li data-forma-linha={forma} className="flex min-w-0 flex-col gap-2 px-3 py-2.5">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="shrink-0 text-tinta-3">{icone}</span>
        <span className="text-sm font-medium">{rotulo}</span>
        {ativa !== undefined && <Selo cor={ativa ? 'verde' : 'neutro'}>{ativa ? t('formasAcesso.ativo') : t('formasAcesso.desativado')}</Selo>}
      </div>
      {valor && <p className="numero min-w-0 text-sm break-all text-tinta-2">{valor}</p>}
      {ajuda && <p className="text-xs text-tinta-3">{ajuda}</p>}
      <div className="flex flex-wrap gap-2">{acoes}</div>
    </li>
  );
}
